import {Observable, of} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {CacheById} from './cache-by-id';
import {IterableUtil, MapUtils, SetUtils} from '../util/collections/collection-utils';

/** Caches items by their own id and by the id of a parent they belong to, keeping one copy of each item.
 *
 * * Fetching by parent id caches each items for both parent and the item's id.
 * * An item can belong to multiple parents (ex: shared favourites are under multiple equipment).
 * * Items are only recorded under parents that were requested so that other parent ids are not incompletely cached.
 * * `cachingEnabled` false makes every read fetch instead of consulting the cache. */
export class CacheByParentId<ParentId, Id, T> {
  private readonly itemsByIdCache: CacheById<Id, T>;
  private readonly idsByParentIdCache: CacheById<ParentId, Set<Id>>;

  constructor(private readonly fetchByIds: (ids: Id[]) => Observable<Map<Id, T>>,
              private readonly fetchByParentIds: (parentIds: ParentId[]) => Observable<Map<ParentId, Map<Id, T>>>,
              private readonly cachingEnabled = true) {
    this.itemsByIdCache = new CacheById(fetchByIds);
    this.idsByParentIdCache = new CacheById((parentIds: ParentId[]) => this.fetchByParentIds(parentIds).pipe(
      mergeMap(itemByIdByParentId => {
        const requestedParentIdSet = new Set(parentIds);
        const itemObservableById = new Map<Id, Observable<T>>();
        const idsByParentId = new Map<ParentId, Set<Id>>();
        itemByIdByParentId.forEach((itemById, parentId) => {
          itemById.forEach((item, id) => itemObservableById.set(id, of(item)));
          if (requestedParentIdSet.has(parentId)) {
            idsByParentId.set(parentId, new Set(itemById.keys()));
          }
        });
        return this.itemsByIdCache.setItems(itemObservableById).pipe(map(() => idsByParentId));
      })
    ));
  }

  /** @param parentIds read first when given, so that the ids they cover need no fetch of their own */
  getByIds(ids: Id[], parentIds?: ParentId[]): Observable<Map<Id, T>> {
    if (!this.cachingEnabled) {
      return this.fetchByIds(ids);
    }
    const parentsRead$ = parentIds?.length ? this.idsByParentIdCache.getByIds(parentIds) : of(null);
    return parentsRead$.pipe(
      mergeMap(() => this.itemsByIdCache.getByIds(ids)),
      // The by id cache keeps an entry for an id nothing came back for, which a fetch does not have
      map(itemById => new Map(IterableUtil.filter(itemById, ([, item]) => item != null)))
    );
  }

  getListByParentId(parentId: ParentId): Observable<T[]> {
    return this.getListByParentIds([parentId]).pipe(map(itemsByParentId => itemsByParentId.get(parentId) ?? []));
  }

  getListByParentIds(parentIds: ParentId[]): Observable<Map<ParentId, T[]>> {
    return this.getByParentIds(parentIds).pipe(
      map(itemByIdByParentId => MapUtils.mapValues(itemByIdByParentId, itemById => Array.from(itemById.values())))
    );
  }

  private getByParentIds(parentIds: ParentId[]): Observable<Map<ParentId, Map<Id, T>>> {
    const itemByIdByParentId$: Observable<Map<ParentId, Map<Id, T>>> = this.cachingEnabled
      ? this.idsByParentIdCache.getByIds(parentIds).pipe(
        mergeMap(idsByParentId => {
          const allIds = new Set<Id>();
          idsByParentId.forEach(ids => SetUtils.addAll(allIds, ids));
          return this.itemsByIdCache.getByIds(Array.from(allIds)).pipe(
            map(itemById => MapUtils.mapValues(idsByParentId, ids => {
              const parentItemById = new Map<Id, T>();
              ids?.forEach(id => {
                const item = itemById.get(id);
                if (item != null) {
                  parentItemById.set(id, item);
                }
              });
              return parentItemById;
            }))
          );
        })
      )
      : this.fetchByParentIds(parentIds);
    // Answer with exactly the parents that were asked for, so that a caller cannot tell the two modes apart
    return itemByIdByParentId$.pipe(map(itemByIdByParentId => new Map(parentIds.map(parentId =>
      <[ParentId, Map<Id, T>]>[parentId, itemByIdByParentId.get(parentId) ?? new Map<Id, T>()]))));
  }
}
